import { useCallback, useMemo, useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Badge,
  Banner,
  BlockStack,
  InlineStack,
  Button,
  Box,
} from '@shopify/polaris';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { useWooBillingStatus, useWooBillingCheckout, useWooBillingPortal } from '../hooks/api';

// WooCommerce billing (Stripe). Self-service: pick a plan -> EMBEDDED Stripe
// Checkout, mounted right here; manage/cancel -> Stripe Customer Portal. The
// plan is enforced server-side via the Stripe webhook -> bridge Shop.plan, so
// this UI only kicks off the flows.
//
// The form is embedded rather than hosted because the Stripe account is shared
// with other products: the hosted page showed another brand's name to a
// Trackful merchant in the middle of paying. Here the surrounding page is ours.
const WOO_PLANS: Array<{ key: string; name: string; blurb: string }> = [
  { key: 'STARTER', name: 'Starter', blurb: 'Google Ads, Meta CAPI, GA4' },
  { key: 'GROWTH', name: 'Growth', blurb: '+ TikTok, Pinterest, Snapchat, advanced analytics' },
  { key: 'PRO', name: 'Pro', blurb: '+ alerts, CSV export, outbound webhooks, API access' },
];

export function BillingWoo() {
  const { data: status, isLoading } = useWooBillingStatus();
  const checkout = useWooBillingCheckout();
  const portal = useWooBillingPortal();
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<{ clientSecret: string; publishableKey: string } | null>(null);
  const [pendingPlan, setPendingPlan] = useState<string | null>(null);

  const currentPlan = status?.plan || 'FREE';

  // loadStripe must not re-run on every render: a new promise remounts the
  // iframe and loses the form the merchant is filling in.
  const stripePromise = useMemo(
    () => (session ? loadStripe(session.publishableKey) : null),
    [session?.publishableKey],
  );

  const choose = useCallback(
    async (plan: string) => {
      setError(null);
      setPendingPlan(plan);
      try {
        const created = await checkout.mutateAsync({ plan });
        setSession(created);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not start checkout.');
      } finally {
        setPendingPlan(null);
      }
    },
    [checkout],
  );

  const manage = useCallback(async () => {
    setError(null);
    try {
      const { url } = await portal.mutateAsync();
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open the billing portal.');
    }
  }, [portal]);

  return (
    <Page title="Billing">
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {error && (
              <Banner tone="critical" onDismiss={() => setError(null)}>
                {error}
              </Banner>
            )}

            {session && stripePromise ? (
              <Card>
                <BlockStack gap="400">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text as="h2" variant="headingMd">
                      Payment
                    </Text>
                    <Button
                      variant="tertiary"
                      onClick={() => {
                        setSession(null);
                        setError(null);
                      }}
                    >
                      Back to plans
                    </Button>
                  </InlineStack>
                  {/* Stripe redirects to the return_url once the payment
                      completes, so there is no success state to render here. */}
                  <EmbeddedCheckoutProvider
                    stripe={stripePromise}
                    options={{ clientSecret: session.clientSecret }}
                  >
                    <EmbeddedCheckout />
                  </EmbeddedCheckoutProvider>
                </BlockStack>
              </Card>
            ) : (
              <>
                <Card>
                  <BlockStack gap="400">
                    <InlineStack align="space-between" blockAlign="center">
                      <Text as="h2" variant="headingMd">
                        Current plan
                      </Text>
                      <Badge tone="success">{isLoading ? '…' : currentPlan}</Badge>
                    </InlineStack>
                    {status?.hasSubscription && (
                      <Box>
                        <Button onClick={manage} loading={portal.isPending}>
                          Manage billing
                        </Button>
                      </Box>
                    )}
                  </BlockStack>
                </Card>

                <Card>
                  <BlockStack gap="400">
                    <Text as="h2" variant="headingMd">
                      Choose a plan
                    </Text>
                    {WOO_PLANS.map((p) => (
                      <InlineStack key={p.key} align="space-between" blockAlign="center">
                        <BlockStack gap="100">
                          <Text as="span" variant="bodyMd" fontWeight="semibold">
                            {p.name}
                          </Text>
                          <Text as="span" tone="subdued" variant="bodySm">
                            {p.blurb}
                          </Text>
                        </BlockStack>
                        <Button
                          variant={p.key === currentPlan ? 'secondary' : 'primary'}
                          disabled={p.key === currentPlan}
                          loading={checkout.isPending && pendingPlan === p.key}
                          onClick={() => choose(p.key)}
                        >
                          {p.key === currentPlan ? 'Current' : 'Choose'}
                        </Button>
                      </InlineStack>
                    ))}
                  </BlockStack>
                </Card>
              </>
            )}
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
